import { routes } from './constants';
import {
  canViewDashboard,
  canAccessClientsPage,
  canAccessAccounting,
  canAccessUsersPage,
  canAccessRequestsPage,
  canAccessPendingJobsTab,
} from './permissions';

export const MOBILE_BOTTOM_TABS_CHANGED_EVENT = 'mobileBottomTabsChanged';
export const MOBILE_BOTTOM_TABS_STORAGE_KEY = 'mobileBottomTabs';
export const DEFAULT_BOTTOM_TAB_COUNT = 5;

/** Sidebar order — must match MainDrawer localRoutes top-level items. */
export const BOTTOM_TAB_DEFINITIONS = [
  {
    id: 'dashboard',
    title: 'Dashboard',
    shortTitle: 'Home',
    path: routes.dashboard,
    matchPrefix: '/dashboard',
    isAccessible: canViewDashboard,
  },
  {
    id: 'clients',
    title: 'Clients',
    shortTitle: 'Clients',
    path: routes.clients,
    matchPrefix: '/clients',
    isAccessible: canAccessClientsPage,
  },
  {
    id: 'jobs',
    title: 'Work Orders',
    shortTitle: 'Jobs',
    path: routes.jobsAll,
    matchPrefix: '/jobs',
    isAccessible: () => true,
  },
  {
    id: 'accounting',
    title: 'Accounting',
    shortTitle: 'Accounting',
    path: routes.accountingReadyForInvoicing,
    matchPrefix: '/accounting',
    isAccessible: canAccessAccounting,
  },
  {
    id: 'users',
    title: 'Users',
    shortTitle: 'Users',
    path: routes.users,
    matchPrefix: '/users',
    isAccessible: canAccessUsersPage,
  },
  {
    id: 'technicians',
    title: 'Techs/Vendors',
    shortTitle: 'Techs',
    path: routes.technicians,
    matchPrefix: '/technicians',
    isAccessible: () => true,
  },
  {
    id: 'requests',
    title: 'Requests',
    shortTitle: 'Requests',
    path: routes.clientRequests,
    matchPrefix: '/client-requests',
    isAccessible: canAccessRequestsPage,
  },
  {
    id: 'reminders',
    title: 'Reminders',
    shortTitle: 'Reminders',
    path: routes.reminders,
    matchPrefix: '/reminders',
    isAccessible: canAccessPendingJobsTab,
  },
  {
    id: 'notifications',
    title: 'Notifications',
    shortTitle: 'Alerts',
    path: routes.notifications,
    matchPrefix: '/notifications',
    isAccessible: () => true,
  },
  {
    id: 'calendar',
    title: 'Calendar',
    shortTitle: 'Calendar',
    path: routes.calendar,
    matchPrefix: '/calendar',
    isAccessible: () => true,
  },
  {
    id: 'map',
    title: 'Map',
    shortTitle: 'Map',
    path: routes.map,
    matchPrefix: '/map',
    isAccessible: () => true,
  },
];

const definitionById = Object.fromEntries(BOTTOM_TAB_DEFINITIONS.map((def) => [def.id, def]));

export function getAvailableBottomTabDefinitions(user) {
  return BOTTOM_TAB_DEFINITIONS.filter((def) => def.isAccessible(user));
}

export function getDefaultBottomTabIds(user) {
  return getAvailableBottomTabDefinitions(user)
    .slice(0, DEFAULT_BOTTOM_TAB_COUNT)
    .map((def) => def.id);
}

export function getDefaultBottomTabOrder(user) {
  return getAvailableBottomTabDefinitions(user).map((def) => def.id);
}

function getUserStorageKey(userId) {
  return `${MOBILE_BOTTOM_TABS_STORAGE_KEY}_${userId || 'anonymous'}`;
}

export function loadMobileBottomTabsConfig(userId) {
  const raw = localStorage.getItem(getUserStorageKey(userId));
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return {
      visible: parsed.visible !== false,
      tabOrder: Array.isArray(parsed.tabOrder) ? parsed.tabOrder : [],
    };
  } catch {
    return null;
  }
}

export function saveMobileBottomTabsConfig(userId, config) {
  localStorage.setItem(getUserStorageKey(userId), JSON.stringify(config));
  window.dispatchEvent(new CustomEvent(MOBILE_BOTTOM_TABS_CHANGED_EVENT));
}

export function sanitizeBottomTabOrder(tabOrder, user) {
  const available = getAvailableBottomTabDefinitions(user);
  const availableIds = available.map((def) => def.id);
  const availableSet = new Set(availableIds);

  const sanitized = (Array.isArray(tabOrder) ? tabOrder : []).filter(
    (id, index, arr) => typeof id === 'string' && availableSet.has(id) && arr.indexOf(id) === index,
  );

  availableIds.forEach((id) => {
    if (!sanitized.includes(id)) sanitized.push(id);
  });

  return sanitized;
}

export function resolveBottomTabs(user) {
  const available = getAvailableBottomTabDefinitions(user);
  const saved = loadMobileBottomTabsConfig(user?.id);
  const tabOrder = sanitizeBottomTabOrder(saved?.tabOrder, user);
  const maxTabs = Math.min(DEFAULT_BOTTOM_TAB_COUNT, available.length);

  const orderedDefinitions = tabOrder
    .map((id) => definitionById[id])
    .filter(Boolean)
    .slice(0, maxTabs);

  return {
    visible: saved?.visible !== false,
    tabs: orderedDefinitions,
    tabOrder,
  };
}

export function matchesBottomTab(pathname, tab) {
  if (!tab?.matchPrefix) return false;
  if (tab.matchPrefix === '/jobs') {
    return pathname.startsWith('/jobs');
  }
  return pathname.startsWith(tab.matchPrefix);
}

export function getActiveBottomTabId(pathname, tabs) {
  const match = tabs.find((tab) => matchesBottomTab(pathname, tab));
  return match?.id ?? null;
}
